import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Send, CheckCircle, Euro, Calendar, Users, Package, AlertCircle } from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api/portal/fornitore';

const STATUS_LABELS = {
  pending: { label: 'In attesa', cls: 'bg-yellow-900/40 text-yellow-300' },
  quoted:  { label: 'Preventivata', cls: 'bg-blue-900/40 text-blue-300' },
  paused:  { label: 'In pausa', cls: 'bg-gray-800 text-gray-400' },
  confirmed: { label: 'Confermata', cls: 'bg-green-900/40 text-green-300' },
};

export default function SupplierPortal() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [prices, setPrices] = useState({});   // { `${reqId}_${svcId}`: price }
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/${token}`);
        setData(res.data);
        // Pre-fill already submitted prices
        const pre = {};
        for (const req of res.data.requests || []) {
          for (const svc of req.services || []) {
            if (svc.submitted_price) {
              pre[`${req.request_id}_${svc.service_id}`] = svc.submitted_price;
            }
          }
        }
        setPrices(pre);
      } catch {
        setError('Link non valido o scaduto. Contatta la struttura per ricevere un nuovo link.');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const priceKey = (reqId, svcId) => `${reqId}_${svcId}`;

  const pendingCount = () => {
    if (!data) return 0;
    return data.requests.reduce((acc, req) =>
      acc + req.services.filter(s => !s.already_quoted).length, 0);
  };

  const handleSubmit = async () => {
    const quotes = [];
    for (const [key, price] of Object.entries(prices)) {
      if (!price || parseFloat(price) <= 0) continue;
      const [reqId, svcId] = key.split('_');
      quotes.push({ request_id: reqId, service_id: svcId, price: parseFloat(price) });
    }
    if (quotes.length === 0) {
      alert('Inserisci almeno un preventivo prima di inviare.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/${token}/submit`, { quotes });
      setSubmitted(true);
      // Reload to show updated state
      const res = await axios.get(`${API}/${token}`);
      setData(res.data);
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore durante l\'invio. Riprova.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--lux-bg)] flex items-center justify-center">
        <Loader2 size={32} className="animate-spin text-[#c9a84c]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[var(--lux-bg)] flex flex-col items-center justify-center px-6 text-center">
        <AlertCircle size={48} className="text-red-400 mb-6" strokeWidth={1} />
        <h1 className="font-serif text-2xl text-white mb-3">Link non valido</h1>
        <p className="text-[var(--lux-text-muted)] max-w-sm">{error}</p>
      </div>
    );
  }

  const { supplier, requests } = data;

  return (
    <div className="min-h-screen bg-[var(--lux-bg)]">
      {/* Header */}
      <header className="border-b border-[var(--lux-border-1)] bg-[var(--lux-surface-1)] px-6 py-5">
        <div className="max-w-3xl mx-auto">
          <p className="text-[#c9a84c] text-[10px] uppercase tracking-[0.2em] mb-1">Portale Fornitore</p>
          <h1 className="font-serif text-2xl text-white font-light">
            {supplier.company || supplier.name}
          </h1>
          {supplier.company && (
            <p className="text-[var(--lux-text-muted-2)] text-sm mt-0.5">{supplier.name}</p>
          )}
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8 space-y-6">
        {/* Success toast */}
        {submitted && (
          <div className="flex items-center gap-3 bg-green-900/30 border border-green-700/50 rounded-lg px-5 py-4">
            <CheckCircle size={20} className="text-green-400 shrink-0" />
            <p className="text-green-300 text-sm">Preventivi inviati con successo. La struttura li riceverà a breve.</p>
          </div>
        )}

        {/* Summary */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-[var(--lux-surface-1)] border border-[var(--lux-border-1)] rounded-lg p-4 text-center">
            <p className="font-serif text-3xl text-white font-light">{requests.length}</p>
            <p className="text-[var(--lux-text-muted-3)] text-xs mt-1">Richieste totali</p>
          </div>
          <div className="bg-[var(--lux-surface-1)] border border-[var(--lux-border-1)] rounded-lg p-4 text-center">
            <p className="font-serif text-3xl text-[#c9a84c] font-light">{pendingCount()}</p>
            <p className="text-[var(--lux-text-muted-3)] text-xs mt-1">Preventivi da inserire</p>
          </div>
          <div className="bg-[var(--lux-surface-1)] border border-[var(--lux-border-1)] rounded-lg p-4 text-center">
            <p className="font-serif text-3xl text-green-400 font-light">
              {requests.reduce((a, r) => a + r.services.filter(s => s.already_quoted).length, 0)}
            </p>
            <p className="text-[var(--lux-text-muted-3)] text-xs mt-1">Già preventivati</p>
          </div>
        </div>

        {requests.length === 0 ? (
          <div className="bg-[var(--lux-surface-1)] border border-[var(--lux-border-1)] rounded-lg p-16 text-center">
            <Package size={36} className="text-[var(--lux-border-3)] mx-auto mb-4" strokeWidth={1} />
            <p className="text-[var(--lux-text-muted-2)] text-sm">Nessuna richiesta in attesa di preventivo.</p>
          </div>
        ) : (
          <>
            {requests.map(req => (
              <div key={req.request_id} className="bg-[var(--lux-surface-1)] border border-[var(--lux-border-1)] rounded-xl overflow-hidden">
                {/* Request header */}
                <div className="px-5 py-4 border-b border-[var(--lux-border-1)] flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="space-y-1">
                    <div className="flex items-center gap-3">
                      <p className="text-white font-medium">{req.property_name}</p>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full ${STATUS_LABELS[req.status]?.cls || 'bg-gray-800 text-gray-400'}`}>
                        {STATUS_LABELS[req.status]?.label || req.status}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-4 text-[var(--lux-text-muted-2)] text-xs">
                      <span className="flex items-center gap-1">
                        <Calendar size={11} /> {req.checkin_date} → {req.checkout_date}
                      </span>
                      <span className="flex items-center gap-1">
                        <Users size={11} /> {req.num_guests} {req.num_guests === 1 ? 'ospite' : 'ospiti'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Services */}
                <div className="divide-y divide-[var(--lux-surface-2)]">
                  {req.services.map(svc => {
                    const key = priceKey(req.request_id, svc.service_id);
                    const isQuoted = svc.already_quoted;
                    return (
                      <div key={svc.service_id} className="px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-4">
                        <div className="flex-1">
                          <p className="text-white text-sm font-medium">{svc.service_name}</p>
                          {svc.details && (
                            <p className="text-[var(--lux-text-muted-2)] text-xs mt-0.5">{svc.details}</p>
                          )}
                          {isQuoted && svc.submitted_price && (
                            <p className="text-green-400 text-xs mt-1 flex items-center gap-1">
                              <CheckCircle size={10} /> Preventivo inserito: €{svc.submitted_price}
                              {svc.submitted_at && (
                                <span className="text-[var(--lux-text-muted-3)] ml-1">
                                  ({new Date(svc.submitted_at).toLocaleDateString('it-IT')})
                                </span>
                              )}
                            </p>
                          )}
                        </div>
                        {/* Price input */}
                        <div className="flex items-center gap-2 shrink-0">
                          <div className="flex items-center bg-[var(--lux-surface-2)] border border-[var(--lux-border-2)] rounded-lg overflow-hidden">
                            <Euro size={13} className="text-[#c9a84c] ml-3" />
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={prices[key] || ''}
                              onChange={e => setPrices(p => ({ ...p, [key]: e.target.value }))}
                              placeholder={isQuoted ? `${svc.submitted_price || '0'}` : '0.00'}
                              data-testid={`price-input-${svc.service_id}`}
                              className="bg-transparent text-white text-sm px-3 py-2.5 w-28 focus:outline-none placeholder-[var(--lux-border-4)]"
                            />
                          </div>
                          {isQuoted && !prices[key] && (
                            <span className="text-[#c9a84c] text-[10px]">aggiorna</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}

            {/* Submit button */}
            <div className="sticky bottom-6 flex justify-center">
              <button
                onClick={handleSubmit}
                disabled={submitting}
                data-testid="submit-quotes-btn"
                className="flex items-center gap-3 bg-[#c9a84c] hover:bg-[#b8973e] text-black text-sm font-medium px-8 py-3.5 rounded-full shadow-2xl transition-all disabled:opacity-60"
              >
                {submitting ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <Send size={16} />
                )}
                {submitting ? 'Invio in corso...' : 'Invia preventivi'}
              </button>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
